import { useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { agentOperationsQuery, runDiagnostic, type DeviceAgentOperationsPage, type DeviceAgentScanOption } from '../api/devices'
import { toast } from '../ui/notify'
import { SettingsCheck, SettingsRadio } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'

/**
 * Panoul unei scanari de diagnostic - diagnostic_reports/device_scan.php:
 * checkbox-uri + Run Scan. Fila Operations a fisei il deschide pe `network_scan`
 * (implicit); caseta Diagnostic din randul desfacut al lui Securitee il deschide
 * pe `security_scan`, PE LOC, fara sa mai iasa din /app.
 *
 * Trimiterea e `Diagnostic_reports::runDiagnostic` (CreateTicket + SendCommands):
 * `POST /devices/{id}/diagnostics/run` cu campurile formularului, ca in
 * `deviceScan` din opteemum.js - la raspuns, tichetul deschis si comanda trimisa.
 *
 * Run Scan se deseneaza numai cand `canRun` e adevarat
 * (`opteemum.live_devices_computer_scan.read`, dreptul cerut de `runDiagnostic`
 * pentru toate trei rapoartele): un buton care intoarce 403 e mai rau decat
 * lipsa lui.
 *
 * Bifele si radiourile sunt `SettingsCheck` / `SettingsRadio`, bifa in desenul
 * nou: pana pe 18 septembrie 2026 erau casutele browserului, albastre, intr-un
 * `div.checkbox` de Bootstrap (bifa `position: absolute`, 20px in stanga).
 * Numele trimise sunt aceleasi: `options[]` si `<cheie>_extended`.
 */

const SCAN_CSS = `.od-scan-option{margin:0 0 10px}
.od-scan-extended{display:flex;gap:16px;margin:6px 0 0 21px}`

export function DeviceAgentOperations({ id, report }: { id: number; report?: string }) {
  const page = useQuery(agentOperationsQuery(id, report))
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return <Form page={page.data} />
}

function Form({ page }: { page: DeviceAgentOperationsPage }) {
  const [picked, setPicked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(page.options.map((o) => [o.key, true])),
  )
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (busy) return
    const form = new FormData(e.currentTarget)
    const options = form.getAll('options[]').map(String)
    const extended: Record<string, string> = {}
    for (const opt of page.options) {
      if (opt.extended && form.get(`${opt.key}_extended`) === '1') extended[`${opt.key}_extended`] = '1'
    }
    setBusy(true)
    try {
      const done = await runDiagnostic(page.device.id, page.report, options, extended)
      if (done.ok) toast(`Scan started - ticket #${done.ticket} opened.`, 'Success', 'success')
      else toast(done.message ?? 'Something went wrong.', 'Error', 'error')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form method="post" action="/diagnostic_reports/runDiagnostic" id="deviceScan" onSubmit={submit}>
      <style>{SCAN_CSS}</style>
      <input type="hidden" name="device" value={page.device.id} />
      <input type="hidden" name="report" value={page.report} />
      {page.options.map((opt) => (
        <OptionRow key={opt.key} option={opt} checked={!!picked[opt.key]} onToggle={(on) => setPicked((p) => ({ ...p, [opt.key]: on }))} />
      ))}
      <div className="row">
        <div className="col-md-12" style={{ margin: 20 }}>
          {page.canRun ? (
            <input type="submit" className="btn btn-danger" value="Run Scan" disabled={busy} />
          ) : (
            <p className="color-light-grey" style={{ margin: 0 }}>
              <i className="fas fa-info-circle"></i> You can see this scan, but not start it.
            </p>
          )}
        </div>
      </div>
    </form>
  )
}

function OptionRow({
  option,
  checked,
  onToggle,
}: {
  option: DeviceAgentScanOption
  checked: boolean
  onToggle(on: boolean): void
}) {
  return (
    <div className="od-scan-option">
      <SettingsCheck
        className="icheck-radio"
        name="options[]"
        value={option.key}
        data-required-options={option.requiredOptions}
        data-is-required={option.required ? 1 : 0}
        checked={checked}
        disabled={option.required}
        onChange={onToggle}
        label={option.name}
      />
      {option.extended ? (
        <div className="od-scan-extended" role="radiogroup" aria-label={`${option.name} depth`}>
          <SettingsRadio className="icheck-radio" name={`${option.key}_extended`} value="0" defaultChecked label="Standard" />
          <SettingsRadio className="icheck-radio" name={`${option.key}_extended`} value="1" label="Extended" />
        </div>
      ) : null}
    </div>
  )
}
